import { useCallback, useEffect, useState } from "react";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { swSettings } from "app/common/services/swSettings";
import { HOME_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { CardsWrapper, ModalContent, ModalFooter, Subtitle, Title } from "./style";
import type { IDefaultHomePageModalProps, ISolutionCardSettings } from "./types";
import { DEFAULT_HOMEPAGE_PREFERENCE_KEY, DEFAULT_HOMEPAGES } from "./constants";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { SolutionCard } from "./components/SolutionCard";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { hasDigitalMarketingPermission, hasMarketResearchPermission } from "app/components/SideBar/utils";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { allTrackers } from "app/services/track/track";

export const DefaultHomePageModal = ({ defaultHomepage, onSelectDigitalSuiteHomePage }: IDefaultHomePageModalProps) => {
    const swNavigator = proNavigator;
    const { isSiUser } = salesSettingsHelper;
    const [isOpen, setIsOpen] = useState(false);
    const translate = useTranslation();
    const solutionSettings: Record<string, ISolutionCardSettings> = {
        DS: {
            owned: hasMarketResearchPermission() || hasDigitalMarketingPermission(),
            illustration: "/images/homepage/Digital inteligence-home-cover.svg",
            defaultHomepage: DEFAULT_HOMEPAGES.DS,
        },
        SI: {
            owned: isSiUser(),
            illustration: "/images/homepage/SI-home-cover.svg",
            defaultHomepage: DEFAULT_HOMEPAGES.SI,
        },
    };

    useEffect(() => {
        const solutionsCount = Object.values(solutionSettings).filter((solution) => solution.owned).length;

        if (solutionsCount > 1 && !defaultHomepage) {
            setIsOpen(true);
        }
    }, [swSettings.user, defaultHomepage]);

    const trackEvent = (packageName) => {
        allTrackers.dispatchTrackEvent("Button", "click", `Set ${packageName || ""} as default homepage from modal`);
    };

    const onCardClick = useCallback(
        (key: DEFAULT_HOMEPAGES) => async () => {
            try {
                PreferencesService.add({ [DEFAULT_HOMEPAGE_PREFERENCE_KEY]: key });
                setIsOpen(false);
                switch (key) {
                    case DEFAULT_HOMEPAGES.DS:
                        trackEvent(key);
                        onSelectDigitalSuiteHomePage(key);
                        break;
                    case DEFAULT_HOMEPAGES.SI:
                        trackEvent(key);
                        swNavigator.go(HOME_PAGE_ROUTE);
                        break;
                    default:
                        swNavigator.go("digitalsuite-home");
                }
            } catch (e) {}
        },
        [],
    );

    return (
        <ProModal
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            showCloseIcon={false}
            customStyles={{
                content: {
                    margin: "auto",
                    width: "auto",
                    padding: "36px 26px 26px 32px",
                },
            }}
        >
            <ModalContent>
                <Title>{translate("modal.default.homepage.title")}</Title>
                <CardsWrapper>
                    {Object.values(solutionSettings).map(
                        (solution, index) =>
                            solution.owned && (
                                <SolutionCard
                                    key={solution.illustration + index}
                                    onSolutionClick={onCardClick(solution.defaultHomepage)}
                                    imageUrl={solution.illustration}
                                />
                            ),
                    )}
                </CardsWrapper>
                <ModalFooter>
                    <Subtitle>{translate("modal.default.homepage.footer")}</Subtitle>
                </ModalFooter>
            </ModalContent>
        </ProModal>
    );
};
